import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import { SCOPES, leagueError } from './CompanyLeagues';
import CompanyEventForm, { NewEventButton, emptyEventDraft,
                           eventCreateBody } from '../../components/common/CompanyEventForm';
import { apiRequest, shortDate } from '../../utils';
import './MemberHome.css';
import './CompanyEvents.css';
import './CompanyLeagues.css';

/* One league: the standing, the days, who is competing, and the rules behind the numbers.

   ★ The standing is the FIRST tab and the widest thing on the screen, because it is the only
   thing here that a pile of events cannot already show you. Everything else on this page exists
   to produce it.

   ★ The grid says what a total is made of. A column per day, a cell per competitor, and a cell
   that is empty means "did not take part" rather than "scored nothing" — two different facts
   that a zero would flatten into one. A cell struck through is a day that happened but does not
   count towards that total, which is what "best 5 of 8" does to the other three; hiding it would
   make the total unexplainable.

   ★ Who is IN is read from the master event's registrations — signing up for a league happens
   once, on the league's own event, through the same RSVP route every company event uses. A day
   then subtracts whoever declined it. No second register, and no sign-up fanning out a row per
   day.

   ★ A day says for itself whether it is played in teams or individually, and the switch is on
   the day row. That is independent of where the league's teams live: a season-teams league can
   hold an individual chess round, and every day still has a leaderboard of its own.

   ★ Adding a day opens the SAME form a programme creates its events with
   (``components/common/CompanyEventForm``) and posts to the same route — a league's master and a
   programme are the same shape, a container a day hangs under. It used to navigate to the
   calendar and leave you to find the create button, which is a detour through a screen that
   answers a different question.

   ★ Nothing here writes points. Closing a day does, and that has its own screen — deliberately,
   because it is the one action in the whole feature that is hard to undo casually. */

const TABS = ['standings', 'days', 'who', 'setup'];

const CompanyLeague = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { leagueId } = useParams();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('standings');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [setup, setSetup] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [finishing, setFinishing] = useState(false);
  // The new-day form. null = closed.
  const [dayDraft, setDayDraft] = useState(null);

  const load = useCallback(() => {
    apiRequest('GET', `/company/league/${leagueId}`)
      .then((res) => { setData(res); setSetup(null); })
      .catch(() => navigate('/leagues'));
  }, [leagueId, navigate]);

  useEffect(() => { load(); }, [load]);

  const call = (method, path, body) => {
    if (busy) return Promise.resolve();
    setBusy(true);
    setNotice(null);
    return apiRequest(method, `/company/league/${leagueId}${path}`, body)
      .then((res) => { if (res && res.league) { setData(res); setSetup(null); } return res; })
      .catch((err) => { setNotice(leagueError(t, err)); throw err; })
      .finally(() => setBusy(false));
  };

  const league = data && data.league;
  const table = (data && data.standings) || [];
  const days = (data && data.days) || [];
  const field = (data && data.competitors) || [];
  const attendees = (data && data.attendees) || [];
  // Only the days that count get a column: a briefing that was explicitly excluded has nothing
  // to show in a grid of points, and a column of blanks reads as a bug.
  const countedDays = days.filter((d) => d.is_counted);

  const saveSetup = () => {
    call('PUT', '', {
      ...setup,
      count_best_of: setup.count_best_of === '' ? null : Number(setup.count_best_of),
    }).catch(() => {});
  };

  const finish = (notify) => {
    call('POST', '/finish', { notify })
      .then(() => setFinishing(false))
      .catch(() => setFinishing(false));
  };

  /* Publishing the league IS publishing its master event, through the route every company
     event uses: it mails and rings whoever has not answered, and puts one card on the feed. A
     league that stays in draft cannot be joined, because the answer buttons live on that page.

     ★ It takes the rounds still ahead with it — a league is announced once, so a day left in
     draft would simply be missing from everybody's calendar. The server says how many moved. */
  const publishLeague = (notify) => {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    apiRequest('POST', `/company/event/${league.master_event_id}/publish`,
               { is_public: true, notify })
      .then((res) => {
        if (res && res.days_published) {
          setNotice(t('leagueDaysPublished', { n: res.days_published }));
        }
        load();
      })
      .catch((err) => setNotice(leagueError(t, err)))
      .finally(() => setBusy(false));
  };

  /* One round on to the calendar, without announcing it again — the invitation went out with
     the league. This is the repair for a day that was created while the league itself was
     still a draft; new days now go live with the league they hang under. */
  const publishDay = (dayId) => {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    apiRequest('POST', `/company/event/${dayId}/publish`, { is_public: true, notify: false })
      .then(() => load())
      .catch((err) => setNotice(leagueError(t, err)))
      .finally(() => setBusy(false));
  };

  /* A day is an ordinary company event hanging under the league's master, so it is created
     through the route every company event uses — POST /api/company/events with the master as
     its container. Nothing about a day is special until it is scored. */
  const addDay = () => {
    if (busy || !dayDraft) return;
    setBusy(true);
    setNotice(null);
    apiRequest('POST', '/company/events',
               eventCreateBody(dayDraft, league.master_event_id, { timeZone: league.time_zone }))
      .then((res) => {
        setDayDraft(null);
        if (res && res.series_created) {
          setNotice(t('seriesCreated', { n: res.series_created + 1 }));
        }
        load();
      })
      .catch((err) => setNotice(leagueError(t, err)))
      .finally(() => setBusy(false));
  };

  if (data === null) {
    return (
      <Base activeNav="home">
        <Card><Back to="/leagues" /><span className="auth__spinner" /></Card>
      </Base>
    );
  }

  return (
    <Base activeNav="home">
      <Card>
        <Back to="/leagues" />
        <Title my="1" className="mt-3">
          <Icon name={league.is_closed ? 'trophy' : 'leaderboard'} theme="primary" className="me-2" />
          {league.name}
        </Title>

        {league.is_closed && league.leader && (
          <p className="lg-winner">
            <Icon name="trophy" className="me-1" />
            {t('leagueWinnerIs', { name: league.leader })}
          </p>
        )}

        <div className="ce-tabs">
          {TABS.map((key) => (
            <button
              key={key}
              type="button"
              className={`ce-tab ${tab === key ? 'is-on' : ''}`}
              onClick={() => setTab(key)}
            >
              {t(`leagueTab_${key}`)}
              {key === 'days' && <span className="ce-tab__n">{days.length}</span>}
              {key === 'who' && <span className="ce-tab__n">{attendees.length}</span>}
            </button>
          ))}
        </div>

        {notice && <p className="ce-note ce-note--warn">{notice}</p>}

        {/* ---- THE STANDING ---- */}
        {tab === 'standings' && (
          <>
            {table.length === 0 ? (
              <p className="ce-note">{t(`leagueStandingsEmpty_${league.subject_type}`)}</p>
            ) : (
              <>
                <p className="bravos-q__hint mb-2">
                  {t('leagueStandingsNote', { ladder: league.scoring_scheme })}
                  {league.count_best_of
                    ? ` ${t('leagueBestOfNote', { n: league.count_best_of })}` : ''}
                </p>
                <div className="lg-scroll">
                  <table className="lg-table">
                    <thead>
                      <tr>
                        <th className="lg-table__rank">#</th>
                        <th>{t(`leagueSubject_${league.subject_type}`)}</th>
                        {countedDays.map((d) => (
                          <th key={d.id} className="lg-table__day" title={d.name}>
                            {shortDate(d.date, i18n.language) || d.name}
                          </th>
                        ))}
                        <th className="lg-table__pts">{t('leaguePoints')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {table.map((row) => (
                        <tr key={row.subject_id}>
                          <td className="lg-table__rank">
                            {/* "=1" rather than "1" twice with no explanation. */}
                            {row.played ? `${row.is_tied ? '=' : ''}${row.rank}` : '–'}
                          </td>
                          <td><span className="lg-name">{row.name}</span></td>
                          {countedDays.map((d) => {
                            const cell = row.days[d.id];
                            if (!cell) return <td key={d.id} className="lg-table__day">–</td>;
                            return (
                              <td
                                key={d.id}
                                className={`lg-table__day ${cell.counts ? '' : 'is-dropped'}`}
                                title={cell.counts ? '' : t('leagueDroppedCell')}
                              >
                                <span className="lg-cellpts">{cell.points}</span>
                                {cell.rank && <span className="lg-cellrank">#{cell.rank}</span>}
                              </td>
                            );
                          })}
                          <td className="lg-table__pts">{row.points}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {!league.is_closed && table.some((r) => r.played) && (
              <div className="ce-actions mt-3">
                {!finishing ? (
                  <button
                    type="button"
                    className="bravos-member__makeadmin"
                    onClick={() => setFinishing(true)}
                  >
                    <Icon name="trophy" className="me-1" />{t('leagueFinish')}
                  </button>
                ) : (
                  <span className="lg-confirm">
                    <span className="ce-note">{t('leagueFinishConfirm')}</span>
                    <button type="button" className="btn btn-primary" disabled={busy}
                            onClick={() => finish(true)}>
                      {t('leagueFinishAndTell')}
                    </button>
                    <button type="button" className="bravos-member__makeadmin" disabled={busy}
                            onClick={() => finish(false)}>
                      {t('leagueFinishQuietly')}
                    </button>
                    <button type="button" className="bravos-member__makeadmin"
                            onClick={() => setFinishing(false)}>
                      {t('cancel')}
                    </button>
                  </span>
                )}
              </div>
            )}
            {league.is_closed && (
              <button
                type="button"
                className="bravos-member__makeadmin mt-3"
                disabled={busy}
                onClick={() => call('PUT', '', { is_closed: false }).catch(() => {})}
              >
                {t('leagueReopen')}
              </button>
            )}
          </>
        )}

        {/* ---- THE DAYS ---- */}
        {tab === 'days' && (
          <>
            <p className="bravos-q__hint mb-2">{t('leagueDaysNote')}</p>

            {/* ★ The answer to "why is my round not on the calendar". A league in draft keeps
                its rounds in draft, and the employee calendar only ever shows published events.
                Publishing is offered HERE as well as on Who is in, because this is the tab you
                are on when you notice. */}
            {!league.is_published && days.length > 0 && (
              <div className="ce-note ce-note--warn">
                <p>{t('leagueDaysDraftNote')}</p>
                <div className="ce-actions">
                  <button type="button" className="btn btn-primary" disabled={busy}
                          onClick={() => publishLeague(true)}>
                    {t('leaguePublishAndInvite')}
                  </button>
                  <button type="button" className="bravos-member__makeadmin" disabled={busy}
                          onClick={() => publishLeague(false)}>
                    {t('leaguePublishQuietly')}
                  </button>
                </div>
              </div>
            )}
            {days.length === 0 && <p className="ce-note">{t('leagueDaysEmpty')}</p>}
            {days.map((d) => (
              <div key={d.id} className="ce-row lg-dayrow">
                <span className="ce-date">
                  <span className="ce-date__day">{(d.date || '').slice(8, 10) || '–'}</span>
                  <span className="ce-date__mon">
                    {shortDate(d.date, i18n.language).replace(/^\d+\s*/, '') || ''}
                  </span>
                </span>
                <span className="ce-row__body">
                  <span className="ce-row__top">
                    <span className="ce-row__name">{d.name}</span>
                    {d.closed_at
                      ? <span className="ce-pill ce-pill--going">{t('leagueDayScored')}</span>
                      : <span className="ce-pill ce-pill--draft">{t('leagueDayNotScored')}</span>}
                    {!d.is_counted && (
                      <span className="ce-pill ce-pill--cancelled">{t('leagueDayExcluded')}</span>
                    )}
                    {d.weight !== 1 && (
                      <span className="ce-pill ce-pill--online">×{d.weight}</span>
                    )}
                    <span className="ce-pill ce-pill--online">
                      {t(`leagueMode_${d.mode}`)}
                    </span>
                    {/* Not on anybody's calendar yet. Worth its own pill: an admin looking for
                        a round that never showed up would otherwise have no clue here. */}
                    {!d.is_public && (
                      <span className="ce-pill ce-pill--cancelled">{t('leagueDayDraft')}</span>
                    )}
                  </span>
                  <span className="ce-row__meta">
                    {d.result_count
                      ? t('leagueDayResults', { count: d.result_count })
                      : t('leagueDayNoResults')}
                  </span>
                  <span className="ce-row__buttons">
                    {!d.is_public && (
                      <button type="button" className="ce-tab" disabled={busy}
                              onClick={() => publishDay(d.id)}>
                        {t('leaguePublishDay')}
                      </button>
                    )}
                    {/* The one setting that decides what this day even ranks. Refused by the
                        server once the day is scored, because a team result and an individual
                        one name different rows. */}
                    <button type="button" className="ce-tab" disabled={busy}
                            onClick={() => call('PUT', `/day/${d.id}`,
                                                { mode: d.mode === 'teams' ? 'individual' : 'teams' })
                              .catch(() => {})}>
                      {d.mode === 'teams' ? t('leagueMakeIndividual') : t('leagueMakeTeams')}
                    </button>
                    <button type="button" className="ce-tab"
                            onClick={() => navigate(`/league/${leagueId}/day/${d.id}`)}>
                      {d.closed_at ? t('leagueSeeResult') : t('leagueCloseDay')}
                    </button>
                    <button type="button" className="ce-tab"
                            onClick={() => navigate(`/company-event/${d.id}/edit`)}>
                      {t('leagueEditDay')}
                    </button>
                    <button type="button" className="ce-tab" disabled={busy}
                            onClick={() => call('PUT', `/day/${d.id}`,
                                                { is_counted: !d.is_counted }).catch(() => {})}>
                      {d.is_counted ? t('leagueExcludeDay') : t('leagueIncludeDay')}
                    </button>
                  </span>
                </span>
              </div>
            ))}
            {/* The same form a programme creates its events with, with this league as the
                container. Repeating stamps out real sibling days, which is how a weekly league
                gets its twelve rounds in one press. */}
            <div className="mt-3">
              {dayDraft ? (
                <CompanyEventForm
                  draft={dayDraft}
                  setDraft={setDayDraft}
                  tags={data.tags}
                  purposeCatalog={data.purpose_catalog}
                  busy={busy}
                  onSave={addDay}
                  onCancel={() => { setDayDraft(null); setNotice(null); }}
                />
              ) : (
                <NewEventButton label={t('leagueAddDay')}
                                onClick={() => setDayDraft(emptyEventDraft())} />
              )}
            </div>
          </>
        )}

        {/* ---- WHO IS IN ---- */}
        {tab === 'who' && (
          <>
            <p className="bravos-q__hint mb-2">{t('leagueWhoNote')}</p>

            {/* ★ A league is signed up for on its own event page, so a master still in draft is
                a league with no door. Publishing goes through the route every company event
                uses — which is what sends the invitation and puts the card on the feed. */}
            {!league.is_published && (
              <div className="ce-note ce-note--warn">
                <p>{t('leagueNotPublished')}</p>
                <div className="ce-actions">
                  <button type="button" className="btn btn-primary" disabled={busy}
                          onClick={() => publishLeague(true)}>
                    {t('leaguePublishAndInvite')}
                  </button>
                  <button type="button" className="bravos-member__makeadmin" disabled={busy}
                          onClick={() => publishLeague(false)}>
                    {t('leaguePublishQuietly')}
                  </button>
                </div>
              </div>
            )}
            {attendees.length === 0 && <p className="ce-note">{t('leagueWhoEmpty')}</p>}
            {attendees.map((p) => (
              <div key={p.id} className="ce-person">
                <span className="ce-person__body">
                  <span className="ce-person__name">{p.name}</span>
                  {p.email && <span className="ce-person__email">{p.email}</span>}
                </span>
              </div>
            ))}

            {/* Inviting and answering both happen on the league's own event page, which is an
                ordinary event page with ordinary RSVP buttons. Nothing is reimplemented here. */}
            <button
              type="button"
              className="bravos-member__makeadmin mt-3"
              onClick={() => navigate(`/company-event/${league.master_event_id}/people`)}
            >
              <Icon name="user-add" className="me-1" />{t('leagueInvitePeople')}
            </button>

            {/* The competing side, when there is one to show: the teams of a season-teams
                league. Read, never maintained — the teams live on an event. */}
            {field.length > 0 && (
              <>
                <hr />
                <p className="bravos-q__hint mb-2">
                  {t(`leagueFieldNote_${league.subject_type}`)}
                </p>
                {field.map((row) => (
                  <div key={row.id} className="ce-person">
                    <span className="ce-person__body">
                      <span className="ce-person__name">{row.name}</span>
                    </span>
                  </div>
                ))}
              </>
            )}
          </>
        )}

        {/* ---- THE RULES ---- */}
        {tab === 'setup' && (
          <div className="bravos-form mt-2">
            {/* Filled from the loaded league on first edit, so the form never fights the
                payload while a save is in flight. */}
            {(() => {
              const form = setup || {
                name: league.name,
                team_scope: league.team_scope,
                scoring_scheme: league.uses_default_scheme ? '' : league.scoring_scheme,
                participation_points: league.participation_points,
                count_best_of: league.count_best_of || '',
                standings_are_public: league.standings_are_public,
              };
              const set = (key) => (e) => {
                const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
                setSetup({ ...form, [key]: value });
              };
              const scored = league.closed_count > 0;
              return (
                <>
                  <label>{t('leagueName')}</label>
                  <input value={form.name} onChange={set('name')} maxLength={50} />

                  <label>{t('leagueTeamsQuestion')}</label>
                  <select value={form.team_scope} onChange={set('team_scope')} disabled={scored}>
                    {SCOPES.map((s) => (
                      <option key={s} value={s}>{t(`leagueScope_${s}`)}</option>
                    ))}
                  </select>
                  <p className="ce-check__hint">
                    {scored ? t('leagueScopeLocked') : t(`leagueScope_${form.team_scope}_hint`)}
                  </p>

                  <label>{t('leagueLadder')}</label>
                  <input value={form.scoring_scheme} onChange={set('scoring_scheme')}
                         placeholder="25,18,15,12,10,8,6,4,2,1" maxLength={200} />
                  {/* ★ The one thing this screen must say out loud: editing the ladder does not
                      move a single point that has already been awarded. */}
                  <p className="ce-check__hint">{t('leagueLadderChangeHint')}</p>

                  <div className="lg-two">
                    <span>
                      <label>{t('leagueParticipation')}</label>
                      <input type="number" min="0" value={form.participation_points}
                             onChange={set('participation_points')} />
                    </span>
                    <span>
                      <label>{t('leagueBestOf')}</label>
                      <input type="number" min="1" value={form.count_best_of}
                             onChange={set('count_best_of')} placeholder={t('leagueBestOfAll')} />
                    </span>
                  </div>

                  <label className="ce-check">
                    <input type="checkbox" checked={form.standings_are_public}
                           onChange={set('standings_are_public')} />
                    <span>{t('leaguePublicStandings')}</span>
                  </label>

                  <div className="ce-actions">
                    <button type="button" className="btn btn-primary"
                            disabled={busy || !setup} onClick={saveSetup}>
                      {t('save')}
                    </button>
                  </div>

                  <hr />
                  {!confirmDelete ? (
                    <button type="button" className="bravos-member__makeadmin"
                            onClick={() => setConfirmDelete(true)}>
                      {t('leagueDelete')}
                    </button>
                  ) : (
                    <>
                      <p className="ce-note ce-note--warn">{t('leagueDeleteWarn')}</p>
                      <div className="ce-actions">
                        <button type="button" className="btn btn-primary" disabled={busy}
                                onClick={() => apiRequest('DELETE', `/company/league/${leagueId}`)
                                  .then(() => navigate('/leagues'))
                                  .catch((err) => setNotice(leagueError(t, err)))}>
                          {t('leagueDeleteYes')}
                        </button>
                        <button type="button" className="bravos-member__makeadmin"
                                onClick={() => setConfirmDelete(false)}>
                          {t('cancel')}
                        </button>
                      </div>
                    </>
                  )}
                </>
              );
            })()}
          </div>
        )}
      </Card>
    </Base>
  );
};

export default CompanyLeague;
